// frontend/src/pages/public/HowItWorks/HowItWorksContent.ts
import type { IconGridItem } from "../../../components/public-info/sections/IconGrid";
import type { StepFlowStep } from "../../../components/public-info/sections/StepFlow";
import type { CTAButton } from "../../../components/public-info/sections/CTASection";

/**
 * How It Works content config (NO layout here)
 * - Keep this file .ts (no JSX).
 * - Icons/illustrations are referenced by public paths (e.g. /icons, /illustrations).
 * - Focused on Family Doctors only.
 */


export const howItWorksHero = {
  eyebrow: "For Ontario Residents",
  title: "Find your family doctor, the easy way",
  subtitle:
    "Search participating family doctors accepting new patients and submit a simple online request. No phone calls or waiting on hold.",
  tone: "brand" as const,
};

export const howItWorksNeedsGrid = {
  id: "needs",
  title: "Search by what matters to you",
  subtitle:
    "Use filters that reflect real-life needs to find the right fit faster.",
  columns: 3 as const,
  items: [
    {
      title: "Location",
      description: "Find doctors near home, work, or school.",
      iconSrc: "/icons/location.svg",
    },
    {
      title: "Language",
      description: "Find doctors who speak the language you're most comfortable using.",
      iconSrc: "/icons/language.svg",
    },
    {
      title: "Availability",
      description: "Filter by clinics with evening or weekend hours.",
      iconSrc: "/icons/clock.svg",
    },
    {
      title: "Accessibility",
      description: "Find clinics with wheelchair access and other features.",
      iconSrc: "/icons/accessibility.svg",
    },
    {
      title: "Provider gender",
      description: "Choose a doctor you feel comfortable with.",
      iconSrc: "/icons/gender.svg",
    },
    {
      title: "Clinic services",
      description: "See what on-site services each clinic offers.",
      iconSrc: "/icons/services.svg",
    },
  ] as Array<IconGridItem & { iconSrc: string }>,
};

export const howItWorksFamilyDoctorFlow = {
  id: "family-doctor-flow",
  title: "4 simple steps to submit a request",
  subtitle:
    "Send a request in minutes. No phone calls, no waiting on hold.",
  steps: [
    {
      title: "Search & filter",
      description: "Find doctors by location, language, gender, and more.",
      iconSrc: "/icons/filter.svg",
    },
    {
      title: "Submit a request",
      description: "Fill out a simple form with your details. Takes about 2 minutes.",
      iconSrc: "/icons/send.svg",
    },
    {
      title: "Clinic reviews",
      description: "Your request goes directly to the clinic for review.",
      iconSrc: "/icons/review.svg",
    },
    {
      title: "Get notified",
      description: "You'll receive an email when there is an update on your request.",
      iconSrc: "/icons/contact.svg",
    },
  ] as Array<StepFlowStep & { iconSrc: string }>,
};

export const howItWorksValueSummary = {
  id: "value",
  title: "Why use AcceptingMD?",
  bullets: [
    "Skip the phone calls. Request care online in minutes.",
    "Find the right fit using filters that matter to you.",
    "Free for patients. No account is required to search or submit a request.",
  ],
};

export const howItWorksFAQ = {
  id: "faq",
  title: "Common questions",
  items: [
    {
      question: "How long does it take to hear back?",
      answer:
        "Response times vary by clinic. Your request goes directly to the clinic, and you'll receive an email when there is an update. If the clinic does not take action within 7 days, the request will expire and you'll be notified.",
    },
    {
      question: "What if my request is not accepted?",
      answer:
        "You can continue searching for other participating family doctors who are accepting new patients. A clinic may be unable to accept a request because of its current capacity or practice scope. Not being accepted by one clinic does not prevent you from submitting a request elsewhere.",
    },
    {
      question: "Do I need to create an account?",
      answer:
        "No account is required. You'll provide an email address so your request can be verified, you can receive status updates, and the clinic can contact you if needed.",
    },
    {
      question: "Is this service free?",
      answer:
        "Yes, AcceptingMD is free for patients. There are no fees to search for participating family doctors or submit a request.",
    },
    {
      question: "Can I submit requests to multiple doctors?",
      answer:
        "You may submit requests to more than one participating doctor. AcceptingMD may limit duplicate active requests within the same clinic.",
    },
    {
      question: "What happens if I'm invited for a Meet & Greet?",
      answer:
        "The clinic will contact you with next steps and arrange the Meet & Greet. An invitation does not by itself confirm that you are attached to the practice. Attachment is completed only after the clinic confirms the process is complete.",
    },
  ],
};

export const howItWorksCtas = {
  id: "next-steps",
  title: "Ready to look for a family doctor?",
  subtitle: "No account needed. Search participating doctors and submit an online request in just a few minutes.",
  buttons: [
    {
      label: "Start Your Search",
      to: "/public/find-family-doctor",
      variant: "primary",
    },
    {
      label: "Learn about the Intake Process",
      to: "/public/intake-process",
      variant: "ghost",
    },
  ] as CTAButton[],
};
